import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Snackbar, Stack, Switch,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { apiHost } from '../../utils/apiHost';

// Bahan baku & resep (stok bahan per cabang; produk "ikut bahan" memotong bahan saat terjual).
// Server: controller/bahanController.js (GET/POST /bahan, /bahan/:uuid/{masuk,opname,riwayat,catat}, GET/PUT /resep/:baranguuid).
// Cabang yang tidak mencatat sebuah bahan tidak terpengaruh: produk tetap memakai stok produk biasa.

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const angka = (v, d = 3) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString('id-ID', { maximumFractionDigits: d }) : '-');
const tanggal = (iso) => (iso ? new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const pesanGalat = (e) => e?.response?.data?.message || e?.message || 'Terjadi kesalahan';

const ALASAN = [
  { key: 'rusak', label: 'Rusak' },
  { key: 'hilang', label: 'Hilang' },
  { key: 'kedaluwarsa', label: 'Kedaluwarsa' },
  { key: 'salah_catat', label: 'Salah catat / salah hitung' },
  { key: 'sampel', label: 'Sampel / konsumsi sendiri' },
  { key: 'lainnya', label: 'Lainnya (isi catatan)' },
];

/** Dialog kecil serbaguna dengan satu tombol simpan. */
const FormDialog = ({ buka, judul, onTutup, onSimpan, proses, galat, children, simpanLabel = 'Simpan' }) => (
  <Dialog open={buka} onClose={proses ? undefined : onTutup} fullWidth maxWidth="xs">
    <DialogTitle>{judul}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {galat && <Alert severity="error">{galat}</Alert>}
        {children}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onTutup} disabled={proses}>Batal</Button>
      <Button variant="contained" onClick={onSimpan} disabled={proses}>{proses ? 'Menyimpan…' : simpanLabel}</Button>
    </DialogActions>
  </Dialog>
);

const TabBahan = ({ cabang, superadmin, daftar, daftarSemua, muat, kabari, semua, setSemua, totalMaster }) => {
  const [cari, setCari] = useState('');
  const [dlg, setDlg] = useState(null); // { jenis: 'tambah'|'ubah'|'masuk'|'opname'|'riwayat'|'catat', bahan? }
  const [f, setF] = useState({});
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const [riwayat, setRiwayat] = useState([]);

  const tampil = useMemo(() => {
    const q = cari.trim().toLowerCase();
    return q ? (daftarSemua || daftar).filter((b) => b.nama.toLowerCase().includes(q)) : daftar; // pencarian menjangkau seluruh master
  }, [daftar, daftarSemua, cari]);

  const buka = (jenis, bahan = null) => {
    setGalat('');
    setF(jenis === 'ubah' && bahan ? { nama: bahan.nama, satuan: bahan.satuan, hargaPerSatuan: bahan.hargaPerSatuan } : {});
    setDlg({ jenis, bahan });
    if (jenis === 'riwayat' && bahan) {
      setRiwayat([]);
      ambil(`${base()}/bahan/${bahan.uuid}/riwayat?cabang=${cabang || ''}`).then((r) => setRiwayat(r.data || [])).catch((e) => setGalat(pesanGalat(e)));
    }
  };
  const tutup = () => setDlg(null);

  const kirim = async (fn, pesan) => {
    setProses(true); setGalat('');
    try {
      await fn();
      kabari(pesan);
      tutup();
      await muat();
    } catch (e) {
      setGalat(pesanGalat(e));
    }
    setProses(false);
  };
  const cfg = { withCredentials: true };
  const sc = cabang ? { cabang } : {};

  const simpan = () => {
    const b = dlg.bahan;
    if (dlg.jenis === 'tambah') {
      return kirim(() => axios.post(`${base()}/bahan`, { nama: f.nama, satuan: f.satuan, hargaPerSatuan: f.hargaPerSatuan || undefined, stokAwal: f.stokAwal || undefined, ...sc }, cfg), 'Bahan ditambahkan');
    }
    if (dlg.jenis === 'ubah') {
      return kirim(() => axios.put(`${base()}/bahan/${b.uuid}`, { nama: f.nama, satuan: f.satuan, hargaPerSatuan: f.hargaPerSatuan === '' ? undefined : f.hargaPerSatuan }, cfg), 'Bahan diperbarui (harga modal resep dihitung ulang)');
    }
    if (dlg.jenis === 'masuk') {
      return kirim(() => axios.post(`${base()}/bahan/${b.uuid}/masuk`, { jumlah: f.jumlah, hargaBeli: f.hargaBeli || undefined, keterangan: f.keterangan || undefined, ...sc }, cfg), 'Stok bahan bertambah');
    }
    if (dlg.jenis === 'opname') {
      return kirim(() => axios.post(`${base()}/bahan/${b.uuid}/opname`, { stokFisik: f.stokFisik, alasan: f.alasan, catatan: f.catatan, ...sc }, cfg), 'Stok bahan dikoreksi');
    }
    if (dlg.jenis === 'catat') {
      return kirim(() => axios.post(`${base()}/bahan/${b.uuid}/catat`, { stokAwal: f.stokAwal || undefined, ...sc }, cfg), 'Bahan dicatat di cabang ini');
    }
    return null;
  };

  const berhenti = async (b) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Berhenti mencatat "${b.nama}" di cabang ini? Produk yang memakainya kembali memakai stok produk biasa.`)) return;
    try {
      await axios.delete(`${base()}/bahan/${b.uuid}/catat?cabang=${cabang || ''}`, cfg);
      kabari('Cabang berhenti mencatat bahan ini');
      await muat();
    } catch (e) {
      kabari(pesanGalat(e), 'error');
    }
  };
  const hapus = async (b) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Hapus bahan "${b.nama}" dari semua cabang?`)) return;
    try {
      await axios.delete(`${base()}/bahan/${b.uuid}`, cfg);
      kabari('Bahan dihapus');
      await muat();
    } catch (e) {
      kabari(pesanGalat(e), 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField size="small" label="Cari bahan" value={cari} onChange={(e) => setCari(e.target.value)} helperText={cari ? 'Pencarian memuat seluruh master bahan' : ' '} />
        {(cabang || !superadmin) && <FormControlLabel control={<Switch size="small" checked={semua} onChange={(e) => setSemua(e.target.checked)} />} label={`Semua bahan${totalMaster ? ` (${totalMaster})` : ''}`} />}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={() => buka('tambah')}>+ Tambah Bahan</Button>
      </Stack>
      {superadmin && !cabang && <Alert severity="info" sx={{ mb: 2 }}>Pilih cabang untuk melihat stok bahan dan mencatat pembelian. Tanpa cabang, hanya daftar bahan (master) yang tampil.</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Bahan</TableCell>
              <TableCell>Satuan</TableCell>
              <TableCell align="right">Stok cabang</TableCell>
              <TableCell align="right">Harga / satuan</TableCell>
              <TableCell align="right">Dipakai resep</TableCell>
              <TableCell>Status cabang</TableCell>
              <TableCell align="right">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tampil.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>Belum ada bahan baku. Tekan "Tambah Bahan".</Typography></TableCell></TableRow>
            )}
            {tampil.map((b) => (
              <TableRow key={b.uuid} hover>
                <TableCell sx={{ fontWeight: 600 }}>{b.nama}</TableCell>
                <TableCell>{b.satuan}</TableCell>
                <TableCell align="right" sx={{ color: b.stok !== null && b.stok < 0 ? 'error.main' : undefined, fontWeight: 700 }}>
                  {b.dicatat ? angka(b.stok) : '-'}{b.dicatat && b.stok < 0 ? ' (perlu dicek)' : ''}
                </TableCell>
                <TableCell align="right">{rupiah(b.hargaPerSatuan)}</TableCell>
                <TableCell align="right">{b.dipakaiResep}</TableCell>
                <TableCell>{b.dicatat ? <Chip size="small" color="success" label="Dicatat" /> : <Chip size="small" label={cabang || !superadmin ? 'Belum dicatat' : '-'} />}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {b.dicatat ? (
                    <>
                      <Button size="small" onClick={() => buka('masuk', b)}>Masuk</Button>
                      <Button size="small" onClick={() => buka('opname', b)}>Opname</Button>
                      <Button size="small" onClick={() => buka('riwayat', b)}>Riwayat</Button>
                    </>
                  ) : (cabang || !superadmin) && <Button size="small" onClick={() => buka('catat', b)}>Catat di cabang ini</Button>}
                  <Button size="small" onClick={() => buka('ubah', b)}>Ubah</Button>
                  {b.dicatat && <Button size="small" color="warning" onClick={() => berhenti(b)}>Berhenti catat</Button>}
                  {superadmin && b.dipakaiResep === 0 && <Button size="small" color="error" onClick={() => hapus(b)}>Hapus</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <FormDialog buka={dlg?.jenis === 'tambah'} judul="Tambah bahan baku" onTutup={tutup} onSimpan={simpan} proses={proses} galat={galat}>
        <TextField label="Nama bahan" value={f.nama || ''} onChange={(e) => setF({ ...f, nama: e.target.value })} autoFocus />
        <TextField label="Satuan (gram, ml, butir, …)" value={f.satuan || ''} onChange={(e) => setF({ ...f, satuan: e.target.value })} />
        <TextField label="Harga per satuan (Rp, opsional)" type="number" value={f.hargaPerSatuan || ''} onChange={(e) => setF({ ...f, hargaPerSatuan: e.target.value })} />
        <TextField label="Stok awal (opsional)" type="number" value={f.stokAwal || ''} onChange={(e) => setF({ ...f, stokAwal: e.target.value })} helperText={cabang || !superadmin ? 'Bahan langsung dicatat di cabang ini.' : 'Tanpa memilih cabang, hanya master bahan yang dibuat.'} />
      </FormDialog>

      <FormDialog buka={dlg?.jenis === 'ubah'} judul={`Ubah ${dlg?.bahan?.nama || ''}`} onTutup={tutup} onSimpan={simpan} proses={proses} galat={galat}>
        <TextField label="Nama bahan" value={f.nama || ''} onChange={(e) => setF({ ...f, nama: e.target.value })} />
        <TextField label="Satuan" value={f.satuan || ''} onChange={(e) => setF({ ...f, satuan: e.target.value })} />
        <TextField label="Harga per satuan (Rp)" type="number" value={f.hargaPerSatuan ?? ''} onChange={(e) => setF({ ...f, hargaPerSatuan: e.target.value })} helperText="Harga modal produk yang memakai bahan ini dihitung ulang." />
      </FormDialog>

      <FormDialog buka={dlg?.jenis === 'masuk'} judul={`Stok masuk: ${dlg?.bahan?.nama || ''}`} onTutup={tutup} onSimpan={simpan} proses={proses} galat={galat}>
        <TextField label={`Jumlah masuk (${dlg?.bahan?.satuan || ''})`} type="number" value={f.jumlah || ''} onChange={(e) => setF({ ...f, jumlah: e.target.value })} autoFocus />
        <TextField label="Harga beli per satuan (Rp, opsional)" type="number" value={f.hargaBeli || ''} onChange={(e) => setF({ ...f, hargaBeli: e.target.value })} helperText="Bila diisi, harga bahan dihitung rata-rata tertimbang." />
        <TextField label="Keterangan (opsional)" value={f.keterangan || ''} onChange={(e) => setF({ ...f, keterangan: e.target.value })} />
      </FormDialog>

      <FormDialog buka={dlg?.jenis === 'opname'} judul={`Opname: ${dlg?.bahan?.nama || ''}`} onTutup={tutup} onSimpan={simpan} proses={proses} galat={galat}>
        <Typography variant="body2" color="text.secondary">Stok sistem sekarang: <b>{angka(dlg?.bahan?.stok)} {dlg?.bahan?.satuan}</b></Typography>
        <TextField label={`Stok fisik (${dlg?.bahan?.satuan || ''})`} type="number" value={f.stokFisik ?? ''} onChange={(e) => setF({ ...f, stokFisik: e.target.value })} autoFocus />
        <TextField select label="Alasan selisih (wajib bila berbeda)" value={f.alasan || ''} onChange={(e) => setF({ ...f, alasan: e.target.value })}>
          {ALASAN.map((a) => <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>)}
        </TextField>
        <TextField label="Catatan" value={f.catatan || ''} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
      </FormDialog>

      <FormDialog buka={dlg?.jenis === 'catat'} judul={`Catat ${dlg?.bahan?.nama || ''} di cabang ini`} onTutup={tutup} onSimpan={simpan} proses={proses} galat={galat} simpanLabel="Catat">
        <TextField label={`Stok awal (${dlg?.bahan?.satuan || ''}, opsional)`} type="number" value={f.stokAwal || ''} onChange={(e) => setF({ ...f, stokAwal: e.target.value })} autoFocus />
      </FormDialog>

      <Dialog open={dlg?.jenis === 'riwayat'} onClose={tutup} fullWidth maxWidth="md">
        <DialogTitle>Riwayat stok: {dlg?.bahan?.nama}</DialogTitle>
        <DialogContent>
          {galat && <Alert severity="error">{galat}</Alert>}
          <Table size="small">
            <TableHead><TableRow><TableCell>Waktu</TableCell><TableCell>Jenis</TableCell><TableCell align="right">Jumlah</TableCell><TableCell align="right">Stok sesudah</TableCell><TableCell>Keterangan</TableCell><TableCell>Petugas</TableCell></TableRow></TableHead>
            <TableBody>
              {riwayat.length === 0 && <TableRow><TableCell colSpan={6} align="center">Belum ada riwayat</TableCell></TableRow>}
              {riwayat.map((r) => (
                <TableRow key={r.uuid}>
                  <TableCell>{tanggal(r.createdAt)}</TableCell>
                  <TableCell>{r.jenis === 'masuk' ? 'Masuk' : r.jenis === 'keluar' ? 'Keluar (terjual)' : 'Penyesuaian'}</TableCell>
                  <TableCell align="right" sx={{ color: r.jenis === 'keluar' || (r.selisih !== null && r.selisih < 0) ? 'error.main' : 'success.main' }}>
                    {r.jenis === 'keluar' ? '−' : r.jenis === 'penyesuaian' ? (r.selisih > 0 ? '+' : '−') : '+'}{angka(r.jumlah)}
                  </TableCell>
                  <TableCell align="right">{r.stokSesudah === null ? '-' : angka(r.stokSesudah)}</TableCell>
                  <TableCell>{r.keterangan}{r.hargaBeli ? ` · beli ${rupiah(r.hargaBeli)}/satuan` : ''}</TableCell>
                  <TableCell>{r.petugas || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={tutup}>Tutup</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

const TabResep = ({ cabang, superadmin, bahanDaftar, kabari }) => {
  const adaCabang = !superadmin || !!cabang;
  const qs = cabang ? `?cabang=${cabang}` : '';
  const { data, mutate } = useSWR(`${base()}/resep${qs}`, ambil, { revalidateOnFocus: false });
  const { data: produkData } = useSWR(adaCabang ? `${base()}/stok-opname/produk${qs}` : null, ambil, { revalidateOnFocus: false });
  const resep = Array.isArray(data?.data) ? data.data : [];
  const produk = Array.isArray(produkData?.data) ? produkData.data : [];

  const [dlg, setDlg] = useState(false);
  const [pilih, setPilih] = useState(null); // produk
  const [baris, setBaris] = useState([]); // { bahan, jumlah }
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');

  const bukaUntuk = async (p) => {
    setGalat('');
    setPilih(p || null);
    setBaris([]);
    setDlg(true);
    if (p) {
      try {
        const r = await ambil(`${base()}/resep/${p.baranguuid}${qs}`);
        const peta = new Map(bahanDaftar.map((b) => [b.uuid, b]));
        setBaris((r.data?.baris || []).map((x) => ({ bahan: peta.get(x.bahanuuid) || { uuid: x.bahanuuid, nama: x.nama, satuan: x.satuan, hargaPerSatuan: x.hargaPerSatuan }, jumlah: String(x.jumlah) })));
      } catch (e) { setGalat(pesanGalat(e)); }
    }
  };

  const biaya = baris.reduce((a, x) => a + (Number(x.jumlah) || 0) * (Number(x.bahan?.hargaPerSatuan) || 0), 0);
  const hargaJual = Number(pilih?.harga) || 0;

  const simpan = async (kosongkan = false) => {
    if (!pilih) { setGalat('Pilih produk dulu'); return; }
    setProses(true); setGalat('');
    try {
      await axios.put(`${base()}/resep/${pilih.baranguuid}`, {
        items: kosongkan ? [] : baris.filter((x) => x.bahan).map((x) => ({ bahanuuid: x.bahan.uuid, jumlah: x.jumlah })),
      }, { withCredentials: true });
      kabari(kosongkan ? 'Resep dihapus' : 'Resep disimpan; harga modal produk diperbarui dari biaya bahan');
      setDlg(false);
      mutate();
    } catch (e) {
      setGalat(pesanGalat(e));
    }
    setProses(false);
  };

  const pilihanProduk = produk.map((p) => ({ ...p, harga: p.harga ?? undefined }));

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Resep menentukan bahan yang terpakai tiap porsi produk terjual dan menghitung harga modal otomatis. Produk tanpa resep, atau yang bahannya tidak dicatat cabang, memakai stok produk biasa.
        </Typography>
        <Button variant="contained" onClick={() => bukaUntuk(null)} disabled={!adaCabang}>+ Buat / Ubah Resep</Button>
      </Stack>
      {!adaCabang && <Alert severity="info" sx={{ mb: 2 }}>Pilih cabang untuk membuat atau mengubah resep.</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produk</TableCell>
              <TableCell align="right">Jumlah bahan</TableCell>
              <TableCell align="right">Biaya / porsi</TableCell>
              <TableCell align="right">Harga jual</TableCell>
              <TableCell align="right">Margin</TableCell>
              <TableCell align="right">Porsi tersedia (cabang)</TableCell>
              <TableCell align="right">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resep.length === 0 && <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>Belum ada resep.</Typography></TableCell></TableRow>}
            {resep.map((r) => {
              const margin = r.harga > 0 && r.biayaPerPorsi !== null ? Math.round(((r.harga - r.biayaPerPorsi) / r.harga) * 100) : null;
              return (
                <TableRow key={r.baranguuid} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{r.namabarang}</TableCell>
                  <TableCell align="right">{r.jumlahBahan}</TableCell>
                  <TableCell align="right">{r.biayaPerPorsi === null ? '-' : rupiah(r.biayaPerPorsi)}</TableCell>
                  <TableCell align="right">{rupiah(r.harga)}</TableCell>
                  <TableCell align="right" sx={{ color: margin !== null && margin < 0 ? 'error.main' : undefined }}>{margin === null ? '-' : `${margin}%`}</TableCell>
                  <TableCell align="right">{r.porsiTersedia === null ? <Typography variant="caption" color="text.secondary">bahan belum dicatat</Typography> : r.porsiTersedia}</TableCell>
                  <TableCell align="right"><Button size="small" disabled={!adaCabang} onClick={() => bukaUntuk({ baranguuid: r.baranguuid, namabarang: r.namabarang, harga: r.harga })}>Ubah</Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dlg} onClose={proses ? undefined : () => setDlg(false)} fullWidth maxWidth="sm">
        <DialogTitle>Resep produk</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {galat && <Alert severity="error">{galat}</Alert>}
            <Autocomplete
              options={pilihanProduk}
              value={pilih}
              onChange={(_, v) => setPilih(v)}
              getOptionLabel={(o) => o.namabarang || ''}
              isOptionEqualToValue={(a, b) => a.baranguuid === b.baranguuid}
              renderInput={(p) => <TextField {...p} label="Produk" />}
              disabled={proses}
            />
            <Typography variant="subtitle2">Bahan per 1 porsi</Typography>
            {baris.map((x, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  sx={{ flex: 1 }}
                  size="small"
                  options={bahanDaftar}
                  value={x.bahan || null}
                  onChange={(_, v) => setBaris(baris.map((y, j) => (j === i ? { ...y, bahan: v } : y)))}
                  getOptionLabel={(o) => `${o.nama} (${o.satuan})`}
                  isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                  renderInput={(p) => <TextField {...p} label="Bahan" />}
                />
                <TextField
                  size="small" type="number" label={x.bahan?.satuan || 'Jumlah'} sx={{ width: 120 }} value={x.jumlah}
                  onChange={(e) => setBaris(baris.map((y, j) => (j === i ? { ...y, jumlah: e.target.value } : y)))}
                />
                <Typography variant="caption" sx={{ width: 80, textAlign: 'right' }}>{rupiah((Number(x.jumlah) || 0) * (Number(x.bahan?.hargaPerSatuan) || 0))}</Typography>
                <IconButton size="small" onClick={() => setBaris(baris.filter((_, j) => j !== i))} aria-label="Hapus baris">✕</IconButton>
              </Stack>
            ))}
            <Button size="small" onClick={() => setBaris([...baris, { bahan: null, jumlah: '' }])} sx={{ alignSelf: 'flex-start' }}>+ Tambah bahan</Button>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between"><Typography>Biaya bahan / porsi</Typography><Typography sx={{ fontWeight: 800 }}>{rupiah(biaya)}</Typography></Stack>
              {hargaJual > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Harga jual {rupiah(hargaJual)}</Typography>
                  <Typography variant="body2" color={hargaJual - biaya < 0 ? 'error' : 'text.secondary'}>Margin {Math.round(((hargaJual - biaya) / hargaJual) * 100)}%</Typography>
                </Stack>
              )}
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => simpan(true)} disabled={proses || !pilih}>Hapus resep</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDlg(false)} disabled={proses}>Batal</Button>
          <Button variant="contained" onClick={() => simpan(false)} disabled={proses || !pilih || baris.length === 0}>{proses ? 'Menyimpan…' : 'Simpan resep'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const BahanBaku = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const [tab, setTab] = useState(0);
  const [cabang, setCabang] = useState('');
  const [notif, setNotif] = useState(null);
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];
  const [semua, setSemua] = useState(false); // tampilkan seluruh master bahan (bukan hanya yang relevan untuk cabang ini)
  const qs = superadmin && cabang ? `?cabang=${cabang}` : '';
  const sambung = qs ? '&' : '?';
  const { data, mutate } = useSWR(`${base()}/bahan${qs}${semua ? `${sambung}semua=1` : ''}`, ambil, { revalidateOnFocus: false });
  const { data: dataSemua, mutate: muatSemua } = useSWR(`${base()}/bahan${qs}${sambung}semua=1`, ambil, { revalidateOnFocus: false });
  const bahanDaftar = Array.isArray(data?.data) ? data.data : [];
  const bahanSemua = Array.isArray(dataSemua?.data) ? dataSemua.data : []; // untuk resep: semua master bisa dipilih
  const kabari = (text, severity = 'success') => setNotif({ severity, text });

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Bahan Baku &amp; Resep</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Catat bahan baku, resep tiap produk, dan stok bahan per cabang. Cabang yang tidak mencatat sebuah bahan tidak terpengaruh.
      </Typography>
      {superadmin && (
        <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => setCabang(e.target.value)} sx={{ minWidth: 220, mb: 2 }}>
          <MenuItem value="">(tanpa cabang)</MenuItem>
          {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
        </TextField>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Bahan baku" />
        <Tab label="Resep produk" />
      </Tabs>
      {tab === 0
        ? <TabBahan cabang={superadmin ? cabang : ''} superadmin={superadmin} daftar={bahanDaftar} daftarSemua={bahanSemua} muat={() => Promise.all([mutate(), muatSemua()])} kabari={kabari} semua={semua} setSemua={setSemua} totalMaster={data?.totalMaster} />
        : <TabResep cabang={superadmin ? cabang : ''} superadmin={superadmin} bahanDaftar={bahanSemua} kabari={kabari} />}
      <Snackbar open={Boolean(notif)} autoHideDuration={5000} onClose={() => setNotif(null)}>
        {notif ? <Alert severity={notif.severity} onClose={() => setNotif(null)}>{notif.text}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default BahanBaku;
